import interact from "interactjs";
import {
  inject,
  nextTick,
  onBeforeUnmount,
  onMounted,
  reactive,
  watch
} from "vue";
import { keyCanvasDimensions } from "./netCanvasInjectionKeys";
import { useNetCoords } from "./netCoordsModifier";

import type { Ref } from "vue";
import type {
  Interactable,
  InteractEvent,
  ResizeEvent
} from "@interactjs/types";
import { useMagicKeys } from "@vueuse/core";

type MaybeRef<T> = T | Ref<T>;

interface DragOptions {
  size: MaybeRef<MinimalRect>;
  visible?: MaybeRef<boolean>;
  gridSize?: MaybeRef<number>;
  globalGridSize?: MaybeRef<number>;
  resizable?: MaybeRef<boolean>;
  draggable?: MaybeRef<boolean>;
  minWidth?: MaybeRef<number>;
  minHeight?: MaybeRef<number>;
  aspectRatioFixed?: MaybeRef<boolean>;
  ignoreFrom?: string;
  onDragStart?: (event: InteractEvent, coords: Point) => unknown;
  onDragMove?: (
    event: InteractEvent,
    coords: Point,
    delta: Point & { gridX: number; gridY: number },
    totalDelta: Point
  ) => unknown;
  onDragEnd?: (event: InteractEvent, coords: Point) => unknown;
  onResizeStart?: (size: MinimalRect) => unknown;
  onResize?: (newSize: MinimalRect) => unknown;
  onResizeEnd?: (event: InteractEvent) => unknown;
  onClick?: (event: InteractEvent) => unknown;
  edges?: MaybeRef<{
    top: boolean;
    right: boolean;
    bottom: boolean;
    left: boolean;
  }>;
}

export function useNetDrag(
  element: MaybeRef<HTMLElement | null>,
  options: DragOptions
) {
  const state = reactive({
    element,
    size: options.size,
    gridSize: options.gridSize ?? 1,
    globalGridSize: options.globalGridSize ?? 10,
    draggable: options.draggable ?? false,
    resizable: options.resizable ?? false,
    minWidth: options.minWidth ?? 1,
    minHeight: options.minHeight ?? 1,

    aspectRatioFixed: options.aspectRatioFixed ?? false,
    ignoreFrom: options.ignoreFrom ?? ".ignore-drag",

    visible: options.visible ?? true,

    onDragStart: options.onDragStart,
    onDragMove: options.onDragMove,
    onDragEnd: options.onDragEnd,
    onResizeStart: options.onResizeStart,
    onResize: options.onResize,
    onResizeEnd: options.onResizeEnd,
    onClick: options.onClick,

    interactHandle: null as Interactable | null,
    dragging: false,
    dragOrigin: {
      element: {
        x: 0,
        y: 0
      },
      cursor: {
        x: 0,
        y: 0
      }
    },
    lastDragCoords: {
      x: 0,
      y: 0
    },
    lastGridDragCoords: {
      x: 0,
      y: 0
    },
    edges: options.edges ?? {
      left: true,
      right: true,
      top: false,
      bottom: true
    }
  });

  const { shift } = useMagicKeys();

  const canvasDimensions = inject(keyCanvasDimensions);

  if (!canvasDimensions) return;

  const { toLocalRect, toLocal, toGlobalRect } = useNetCoords({
    gridSize: state.gridSize,
    canvasDimensions
  });

  /**
   * Bind the event listeners to the element.
   */
  function mountInteractions() {
    if (state.element == null) return;

    if (!state.draggable && !state.resizable) return;

    state.interactHandle = interact(state.element).rectChecker(() => {
      const { x, y, height, width } = state.size;
      return toGlobalRect({
        left: x,
        right: x + width,
        top: y,
        bottom: y + height
      });
    });

    if (state.draggable) {
      state.interactHandle = state.interactHandle
        .draggable({
          ignoreFrom: state.ignoreFrom
        })
        .on("dragstart", (event) => {
          const coords = toLocal(event.page);
          onDragStart(event, coords);
        })
        .on("dragmove", (event) => {
          const coords = toLocal(event.page);
          onDragMove(event, coords);
        })
        .on("dragend", (event) => {
          const coords = toLocal(event.page);
          onDragEnd(event, coords);
        });
    }
    if (state.resizable) {
      state.interactHandle = state.interactHandle
        .resizable({
          // resize from all edges and corners
          edges: state.edges
        })
        .on("resizestart", (event) => {
          const rect = toLocalRect(event.rect);
          onResizeStart(event, rect);
        })
        .on("resizemove", (event) => {
          const rect = toLocalRect(event.rect);
          onResize(event, rect);
        })
        .on("resizeend", (event) => {
          onResizeEnd(event);
        });
    }
  }

  function onDragStart(event: InteractEvent, coords: Point) {
    if (!state.draggable) {
      // end the current action
      event.interaction.end();
      // stop all further listeners from being called
      event.stopImmediatePropagation();
      return;
    }

    state.dragging = true;
    state.dragOrigin = {
      element: { x: state.size.x, y: state.size.y },
      cursor: { ...coords }
    };
    state.lastDragCoords = { ...coords };
    state.lastGridDragCoords = { ...coords };

    if (state.onDragStart) {
      state.onDragStart(event, coords);
    }
  }

  function onDragMove(event: InteractEvent, coords: Point) {
    if (!state.draggable) return;

    const deltaX = coords.x - state.lastDragCoords.x;
    const deltaY = coords.y - state.lastDragCoords.y;

    if (state.dragging === false) {
      if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
        state.dragging = true;
      }
    }

    const x = toGridSize(deltaX);
    const y = toGridSize(deltaY);

    const gridDeltaX = coords.x - state.lastGridDragCoords.x;
    const gridDeltaY = coords.y - state.lastGridDragCoords.y;
    const gridX = toGridSize(gridDeltaX, state.globalGridSize);
    const gridY = toGridSize(gridDeltaY, state.globalGridSize);

    if (x !== 0 || y !== 0) {
      state.lastDragCoords.x += x;
      state.lastDragCoords.y += y;

      if (gridX !== 0 || gridY !== 0) {
        state.lastGridDragCoords.x += gridX;
        state.lastGridDragCoords.y += gridY;
      }

      if (state.onDragMove) {
        state.onDragMove(
          event,
          coords,
          { x, y, gridX, gridY },
          {
            x: state.lastDragCoords.x - state.dragOrigin.cursor.x,
            y: state.lastDragCoords.y - state.dragOrigin.cursor.y
          }
        );
      }
    }
  }

  function onDragEnd(pointerEvent: InteractEvent, coords: Point) {
    if (!state.draggable) return;

    if (state.dragging) {
      const deltaX = coords.x - state.lastDragCoords.x;
      const deltaY = coords.y - state.lastDragCoords.y;

      const x = toGridSize(deltaX);
      const y = toGridSize(deltaY);

      const gridDeltaX = coords.x - state.lastGridDragCoords.x;
      const gridDeltaY = coords.y - state.lastGridDragCoords.y;
      const gridX = toGridSize(gridDeltaX, state.globalGridSize);
      const gridY = toGridSize(gridDeltaY, state.globalGridSize);

      if (x !== 0 || y !== 0) {
        state.lastDragCoords.x += x;
        state.lastDragCoords.y += y;

        if (gridX !== 0 || gridY !== 0) {
          state.lastGridDragCoords.x += gridX;
          state.lastGridDragCoords.y += gridY;
        }

        if (state.onDragMove) {
          state.onDragMove(
            pointerEvent,
            { x, y },
            { x: 0, y: 0, gridX, gridY },
            {
              x: state.lastDragCoords.x - state.dragOrigin.cursor.x,
              y: state.lastDragCoords.y - state.dragOrigin.cursor.y
            }
          );
        }
      }

      state.dragging = false;
    } else {
      if (state.onClick) {
        state.onClick(pointerEvent);
      }
    }

    state.dragOrigin = { element: { x: 0, y: 0 }, cursor: { x: 0, y: 0 } };
    state.lastDragCoords = { x: 0, y: 0 };

    if (state.onDragEnd) {
      state.onDragEnd(pointerEvent, coords);
    }
  }

  // ======= Resize Functions =======

  const resizeStartSize: MinimalRect = { x: 0, y: 0, width: 0, height: 0 };

  function onResizeStart(event: Partial<ResizeEvent>, rect: Rect) {
    if (!state.resizable) return;

    // Store the initial size and position
    resizeStartSize.x = rect.left;
    resizeStartSize.y = rect.top;
    resizeStartSize.width = rect.width;
    resizeStartSize.height = rect.height;

    if (state.onResizeStart) {
      state.onResizeStart(resizeStartSize);
    }
  }

  /**
   * resizing function changing the size of the element
   * called by interact resizable
   * @param event (ResizeEvent)
   */
  function onResize(event: Partial<ResizeEvent>, rect: Rect) {
    if (!state.resizable) return;
    let { x, y, height, width } = state.size;
    const aspectRatio = resizeStartSize.width / resizeStartSize.height;

    const bottomOld = y + height;
    const rightOld = x + width;

    // horizontal limit checks
    if (rect.width >= state.minWidth) {
      width = rect.width;
      x = rect.left;
    } else {
      width = state.minWidth;
    }

    // vertical limit checks
    if (rect.height >= state.minHeight) {
      height = rect.height;
      y = rect.top;
    } else {
      height = state.minHeight;
    }

    if (shift.value || state.aspectRatioFixed) {
      // if shift is pressed, keep the aspect ratio
      // Keep in mind that multiple edges (vertical & horizontal) can be resized at the same time
      if (event.edges?.left || event.edges?.right) {
        // horizontal resize
        height = toGridSize(width / aspectRatio);
        if (height < state.minHeight) {
          height = state.minHeight;
        }
        if (!event.edges?.bottom && !event.edges?.top) {
          // if only left edge is resized, adjust y position
          y = toGridSize(y - (height - resizeStartSize.height) / 2);
        } else if (event.edges?.top) {
          // if top edge is resized, adjust y position
          y = toGridSize(bottomOld - height);
        }
      } else if (event.edges?.top || event.edges?.bottom) {
        // vertical resize
        width = toGridSize(height * aspectRatio);
        if (width < state.minWidth) {
          width = state.minWidth;
        }
        if (!event.edges?.left && !event.edges?.right) {
          // if only bottom edge is resized, adjust x position
          x = toGridSize(x - (width - resizeStartSize.width) / 2);
        }
      }
    } else {
      if (event.edges?.left) {
        x = rightOld - width;
      }
      if (event.edges?.top) {
        y = bottomOld - height;
      }
    }

    if (state.onResize) {
      state.onResize({
        x,
        y,
        height,
        width
      });
    }
  }
  function onResizeEnd(event: InteractEvent) {
    if (!state.resizable) return;
    if (state.onResizeEnd) {
      state.onResizeEnd(event);
    }
  }

  function unset() {
    if (state.element) {
      state.interactHandle = null;
      interact(state.element).unset();
    }
  }

  onMounted(() => {
    if (state.draggable || state.resizable) {
      mountInteractions();
    }
  });
  onBeforeUnmount(() => {
    if (state.interactHandle) {
      unset();
    }
  });

  watch(() => state.draggable || state.resizable, rebindIfNeeded);
  watch(
    () =>
      `${state.edges.left}${state.edges.right}${state.edges.top}${state.edges.bottom}`,
    rebindIfNeeded
  );

  function rebindIfNeeded(value: unknown, oldValue: unknown) {
    if (value !== oldValue) {
      if (state.interactHandle) {
        unset();
      }
      mountInteractions();
    }
  }

  watch(
    () => state.visible,
    (value) => {
      if (value == false) {
        if (state.interactHandle) {
          unset();
        }
      } else {
        nextTick(() => mountInteractions());
      }
    }
  );

  watch(
    () => state.minHeight,
    (value) => {
      onResize(
        { edges: { ...state.edges } },
        {
          x: state.size.x,
          y: state.size.y,
          width: state.size.width,
          height: state.size.height,
          left: state.size.x,
          top: state.size.y,
          right: state.size.x + state.size.width,
          bottom: state.size.y + state.size.height
        }
      );
    }
  );

  function toGridSize(value: number, gridSize = state.gridSize) {
    if (state.gridSize <= 0) return value;
    return Math.round(value / gridSize) * gridSize;
  }

  return state.interactHandle;
}

export interface MinimalRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface Rect extends MinimalRect {
  left: number;
  top: number;
  right: number;
  bottom: number;
}

interface Point {
  x: number;
  y: number;
}
